/**
 * Запросы к `/api/public/*` endpoint-ам (BE-7, BE-8, BE-14, FE-15).
 * Используют общий axios-клиент из `api.ts` (FE-3).
 *
 * Все ответы прогоняются через нормализаторы: пустое тело, отсутствующие
 * поля или мусор вместо ожидаемых типов превращаются в безопасные
 * значения (`null` / `[]` / отфильтрованные элементы), чтобы компоненты
 * могли слепо обращаться к полям и рисовать «пустые» состояния вместо
 * падения рендера. Сетевые/HTTP-ошибки по-прежнему реджектятся —
 * их обрабатывает вызывающий код.
 */
import { api } from './api';
import { TOP_CODE_REGION_BY_KATO } from '../consts/topCodeRegion';
import type {
  BudgetIncomeRow,
  FoodIndicator,
  IndicatorDetailResponse,
  IndicatorDetailRow,
  IndicatorRow,
  KatoCoordinatesResponse,
  PopulationDynamicsDetails,
  PopulationDynamicsPoint,
  PublicGeneralResponse,
  PublicMediaMonitoringResponse,
  ScreeningHumanResources,
  ScreeningResponse,
  ScreeningSmbDevelopment,
  SerIndicator,
  SmbRegisteredSubject,
} from '../types/public';

export interface KatoListItem {
  kato: number;
  name_ru: string;
  name_kz: string;
  level: number | null;
  parent_id: number | null;
}

// --- примитивы нормализации ---

const asObj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};

const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/**
 * Число из «свободного» jsonb (скрининг): принимает и число, и числовую
 * строку («» и мусор → null).
 */
const numLoose = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) {
    return Number(v);
  }
  return null;
};

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

// --- нормализаторы DTO ---

/**
 * Достаёт агрегат медиамониторинга: поддерживает и плоский объект
 * (`{clickable_count: ...}`), и словарь по КАТО (`{"590000000": {...}}`).
 */
const clickableFromAgg = (raw: unknown, kato?: number): number | null => {
  const obj = asObj(raw);
  const scoped = kato != null && obj[String(kato)] != null
    ? asObj(obj[String(kato)])
    : obj;
  return num(scoped.clickable_count) ?? num(scoped.total_publication_count);
};

/**
 * Список записей basic_stats: массив (`[{type, ...}, ...]`),
 * словарь по КАТО со списком внутри или `{items: [...]}`.
 */
const statsList = (raw: unknown, kato?: number): unknown[] => {
  if (Array.isArray(raw)) return raw;
  const obj = asObj(raw);
  const scoped = kato != null ? obj[String(kato)] : undefined;
  return Array.isArray(scoped) ? scoped : asArr(obj.items);
};

/** ИФО из basic_stats по type. */
const ifoFromStats = (
  raw: unknown,
  type: string,
  kato?: number,
): number | null => {
  const list = statsList(raw, kato);
  for (const entry of list) {
    const e = asObj(entry);
    const entryKato = num(e.kato);
    if (str(e.type) === type && (entryKato === null || entryKato === kato)) {
      return num(e.ifo);
    }
  }
  return null;
};

/**
 * Показатели СЭР из basic_stats (type='SER'): subtype + ifo +
 * current_year_val. Ответ содержит дубли записей — берём первую по subtype.
 */
const serIndicatorsFromStats = (
  raw: unknown,
  kato?: number,
): SerIndicator[] => {
  const seen = new Set<string>();
  return statsList(raw, kato).flatMap((entry) => {
    const e = asObj(entry);
    const subtype = str(e.subtype);
    if (str(e.type) !== 'SER' || subtype === null || seen.has(subtype)) {
      return [];
    }
    seen.add(subtype);
    return [
      {
        subtype,
        ifo: num(e.ifo),
        currentYearVal: num(e.current_year_val),
        previousYearVal: num(e.previous_year_val),
      },
    ];
  });
};

/**
 * Показатели самообеспеченности из basic_stats (type='FOOD'):
 * subtype + current/previous_year_val (%). Имена полей принимаются
 * и в snake_case, и в camelCase; дубли по subtype отбрасываются.
 */
const foodIndicatorsFromStats = (
  raw: unknown,
  kato?: number,
): FoodIndicator[] => {
  const seen = new Set<string>();
  return statsList(raw, kato).flatMap((entry) => {
    const e = asObj(entry);
    const subtype = str(e.subtype);
    if (str(e.type) !== 'FOOD' || subtype === null || seen.has(subtype)) {
      return [];
    }
    seen.add(subtype);
    return [
      {
        subtype,
        currentYearVal: numLoose(e.current_year_val ?? e.currentYearVal),
        previousYearVal: numLoose(e.previous_year_val ?? e.previousYearVal),
      },
    ];
  });
};

/** Число по первому непустому из ключей (snake_case / camelCase). */
const pickNum = (
  obj: Record<string, unknown>,
  ...keys: string[]
): number | null => {
  for (const key of keys) {
    if (obj[key] != null) return numLoose(obj[key]);
  }
  return null;
};

/**
 * Точки «Динамики численности населения» из population_dynamics:
 * список `{year, value}`; дубли по году отбрасываются, сортировка по году.
 */
const populationDynamicsFrom = (raw: unknown): PopulationDynamicsPoint[] => {
  const seen = new Set<number>();
  return asArr(raw)
    .flatMap((entry) => {
      const e = asObj(entry);
      const year = num(e.year);
      if (year === null || seen.has(year)) return [];
      seen.add(year);
      return [{ year, value: num(e.value) }];
    })
    .sort((a, b) => a.year - b.year);
};

/** Детализация населения из population_dynamics_details (или null). */
const populationDetailsFrom = (
  raw: unknown,
): PopulationDynamicsDetails | null => {
  if (raw == null) return null;
  const e = asObj(raw);
  return {
    male_count: num(e.male_count),
    female_count: num(e.female_count),
    birth_count: num(e.birth_count),
    death_count: num(e.death_count),
    natural_count: num(e.natural_count),
    immigration_count: num(e.immigration_count),
    emigration_count: num(e.emigration_count),
    saldo_migration: num(e.saldo_migration),
    population_employment_count: num(e.population_employment_count),
    youth_population_count: num(e.youth_population_count),
    employed_population_count: num(e.employed_population_count),
    hired_population_count: num(e.hired_population_count),
    self_employed_population_count: num(e.self_employed_population_count),
    unemployed_population_count: num(e.unemployed_population_count),
  };
};

export const normalizeGeneralResponse = (
  raw: unknown,
  kato?: number,
): PublicGeneralResponse => {
  const root = asObj(raw);
  const general = asObj(root.general);
  const akim = asObj(root.akim);
  const nationality =
    root.nationality == null ? null : asObj(root.nationality);

  return {
    general: {
      population: num(general.population),
      avg_salary: num(general.avg_salary),
      area_km2: num(general.area_km2),
      saldo_migration: num(general.saldo_migration),
      unemployed: num(general.unemployed),
      village_count: num(general.village_count),
      year: num(general.year) ?? new Date().getFullYear(),
    },
    akim: {
      name_ru: str(akim.name_ru),
      name_kz: str(akim.name_kz),
      position_ru: str(akim.position_ru),
      position_kz: str(akim.position_kz),
      phone: str(akim.phone),
      in_position_from: str(akim.in_position_from),
      image_url: str(akim.image_url),
    },
    // Все числовые поля, кроме year, — этносы (состав ключей задаёт бэк).
    nationality: nationality && {
      year: num(nationality.year),
      groups: Object.fromEntries(
        Object.entries(nationality).flatMap(([key, value]) => {
          if (key === 'year') return [];
          const count = num(value);
          return count === null ? [] : [[key, count]];
        }),
      ),
    },
    children: asArr(root.children).flatMap((child) => {
      const c = asObj(child);
      const childKato = num(c.kato);
      if (childKato === null) return [];
      return [
        {
          kato: childKato,
          name_ru: str(c.name_ru) ?? '',
          name_kz: str(c.name_kz) ?? '',
          population: num(c.population),
        },
      ];
    }),
    serStats: {
      clickablePublications: clickableFromAgg(root.media_monitoring_agg, kato),
      ifoInvestments: ifoFromStats(root.basic_stats, 'investments', kato),
      ifoIndustry: ifoFromStats(root.basic_stats, 'industry', kato),
    },
    serIndicators: serIndicatorsFromStats(root.basic_stats, kato),
    foodIndicators: foodIndicatorsFromStats(root.basic_stats, kato),
    populationDynamics: populationDynamicsFrom(root.population_dynamics),
    populationDynamicsDetails: populationDetailsFrom(
      root.population_dynamics_details,
    ),
  };
};

/**
 * Первичные данные показателя (drill-through US-02 FR-4.6). Строки без
 * единого заполненного значения отбрасываются: бэк отдаёт `has_data=true`
 * и на «пустых» записях (например, SER/MINING_INDUSTRY у района — все
 * поля null), а показывать пустую таблицу незачем. Дубли (одна и та же
 * запись под разными id — так приходит со staging) сворачиваются в одну.
 */
export const normalizeIndicatorDetail = (
  raw: unknown,
): IndicatorDetailResponse => {
  const root = asObj(raw);
  const seen = new Set<string>();
  const rows: IndicatorDetailRow[] = [];
  for (const entry of asArr(root.rows)) {
    const r = asObj(entry);
    const row: IndicatorDetailRow = {
      id: num(r.id),
      previousYearVal: num(r.previous_year_val),
      currentYearVal: num(r.current_year_val),
      increaseVal: num(r.increase_val),
      changePct: num(r.change_pct),
      ifo: num(r.ifo),
      ifoPlace: num(r.ifo_place),
      additionalInfo: str(r.additional_info),
    };
    const filled =
      row.previousYearVal !== null ||
      row.currentYearVal !== null ||
      row.increaseVal !== null ||
      row.changePct !== null ||
      row.ifo !== null ||
      row.ifoPlace !== null ||
      row.additionalInfo !== null;
    if (!filled) continue;
    // Ключ дедупа — сами значения (id у дублей разные).
    const key = JSON.stringify({ ...row, id: null });
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(row);
  }
  return {
    hasData: root.has_data === true && rows.length > 0,
    rows,
  };
};

export const normalizeMediaMonitoringResponse = (
  raw: unknown,
): PublicMediaMonitoringResponse => ({
  publications: asArr(asObj(raw).publications).flatMap((pub) => {
    const p = asObj(pub);
    const text = str(p.text);
    // Публикация без текста — нечего показывать, отбрасываем.
    if (text === null) return [];
    return [
      {
        text,
        social_net: num(p.social_net) ?? 0,
        like_sum: num(p.like_sum) ?? 0,
        comment_sum: num(p.comment_sum) ?? 0,
        shared_sum: num(p.shared_sum) ?? 0,
        url: str(p.url),
        tone: num(p.tone),
        tone_text: str(p.tone_text),
        audience_size: num(p.audience_size),
      },
    ];
  }),
});

export const normalizeKatoCoordinatesResponse = (
  raw: unknown,
): KatoCoordinatesResponse => ({
  coordinates: asArr(asObj(raw).coordinates).flatMap((coord) => {
    const c = asObj(coord);
    const kato = num(c.kato);
    const latitude = num(c.latitude);
    const longitude = num(c.longitude);
    // Маркер без валидных координат отрисовать нельзя.
    if (kato === null || latitude === null || longitude === null) return [];
    return [
      {
        kato,
        name_ru: str(c.name_ru) ?? '',
        name_kz: str(c.name_kz) ?? '',
        latitude,
        longitude,
      },
    ];
  }),
});

/**
 * Строки `/public/indicators/paired` (parsed_indicators, плоская
 * объединённая форма): массив `{indicator_name_ru/kz, indicator_val,
 * indicator_val2}`. Для образования val — заведения (шт.),
 * val2 — учащиеся (чел.); для здравоохранения val — объекты (шт.),
 * val2 — null. Строки без названия отбрасываются; старые имена полей
 * (v1 `indicatorVal_pieces`/`_people`) принимаются как fallback.
 */
export const normalizeIndicatorsResponse = (raw: unknown): IndicatorRow[] => {
  const rootObj = asObj(raw);
  const list = Array.isArray(raw)
    ? raw
    : asArr(rootObj.items ?? rootObj.indicators);
  return list.flatMap((entry) => {
    const e = asObj(entry);
    const name_ru = str(e.indicator_name_ru ?? e.indicatorNameRu);
    if (name_ru === null) return [];
    return [
      {
        name_ru,
        name_kz: str(e.indicator_name_kz ?? e.indicatorNameKz),
        pieces: pickNum(
          e,
          'indicator_val',
          'indicatorVal',
          'indicator_val_pieces',
          'indicatorVal_pieces',
        ),
        people: pickNum(
          e,
          'indicator_val2',
          'indicatorVal2',
          'indicator_val_people',
          'indicatorVal_people',
        ),
      },
    ];
  });
};

export const normalizeKatoList = (raw: unknown): { items: KatoListItem[] } => ({
  items: asArr(asObj(raw).items).flatMap((item) => {
    const i = asObj(item);
    const kato = num(i.kato);
    if (kato === null) return [];
    return [
      {
        kato,
        name_ru: str(i.name_ru) ?? '',
        name_kz: str(i.name_kz) ?? '',
        level: num(i.level),
        parent_id: num(i.parent_id),
      },
    ];
  }),
});

// --- запросы ---

/**
 * In-flight дедупликация /public/general: сайдбар (ContextSidebar) и страница
 * (DigitalPassportPage / PopulationSection) монтируются одновременно и просят
 * один и тот же КАТО — без дедупа это два одинаковых HTTP-запроса.
 * Ключ — kato+year; запись живёт только пока запрос в полёте.
 */
const inflightGeneral = new Map<string, Promise<PublicGeneralResponse>>();

const requestGeneral = async (
  kato: number,
  year?: number,
): Promise<PublicGeneralResponse> => {
  const params: Record<string, number> = { kato };
  if (year !== undefined) {
    params.year = year;
  }
  const response = await api.get<unknown>('/public/general', { params });
  return normalizeGeneralResponse(response.data, kato);
};

export const getGeneral = (
  kato: number,
  year?: number,
): Promise<PublicGeneralResponse> => {
  const key = `${kato}|${year ?? ''}`;
  const existing = inflightGeneral.get(key);
  if (existing) return existing;

  const request = requestGeneral(kato, year).finally(() => {
    inflightGeneral.delete(key);
  });
  inflightGeneral.set(key, request);
  return request;
};

/**
 * Строки «Доходов бюджета» (`GET /api/public/budget-income`).
 * Контракт — СТАТУС бэка от 23.07.2026: строки «Налоговые поступления» /
 * «Неналоговые» / «Поступления от продажи основного капитала» с полями
 * plan_yearly / plan_period / fact_period / execution_pct. Точная форма
 * ответа до деплоя не видна — принимаем массив или {items|rows: [...]},
 * ключи в snake_case/camelCase, строку распознаём по названию.
 */
export const normalizeBudgetIncome = (raw: unknown): BudgetIncomeRow[] => {
  const rootObj = asObj(raw);
  const list = Array.isArray(raw)
    ? raw
    : asArr(rootObj.items ?? rootObj.rows);
  return list.flatMap((entry) => {
    const e = asObj(entry);
    const name_ru = str(
      e.name_ru ?? e.nameRu ?? e.name ?? e.title ?? e.income_type,
    );
    const lower = (name_ru ?? '').toLowerCase();
    const key = lower.includes('неналог')
      ? 'nonTax'
      : lower.includes('налог')
        ? 'tax'
        : lower.includes('капитал') || lower.includes('продаж')
          ? 'assetSales'
          : name_ru;
    if (key == null) return [];
    return [
      {
        key,
        name_ru,
        planYearly: pickNum(e, 'plan_yearly', 'planYearly'),
        planPeriod: pickNum(e, 'plan_period', 'planPeriod'),
        factPeriod: pickNum(e, 'fact_period', 'factPeriod'),
        executionPct: pickNum(e, 'execution_pct', 'executionPct'),
      },
    ];
  });
};

/**
 * «Доходы бюджета» по КАТО района. Ключ эндпоинта — top_code_region
 * (бюджетная классификация), маппинг — TOP_CODE_REGION_BY_KATO;
 * КАТО без кода → null без запроса (виджеты покажут «Нет данных»).
 */
export const getBudgetIncome = async (
  kato: number,
): Promise<BudgetIncomeRow[] | null> => {
  const topCodeRegion = TOP_CODE_REGION_BY_KATO[kato];
  if (topCodeRegion == null) return null;
  const response = await api.get<unknown>('/public/budget-income', {
    params: { top_code_region: topCodeRegion },
  });
  return normalizeBudgetIncome(response.data);
};

/**
 * Показатели-«парсинг» (`GET /api/public/indicators/paired`):
 * образование — modulesType=2, здравоохранение — 3 (Аналитика → Соцсфера).
 * year не передан → последний год на бэке.
 */
export const getIndicators = async (
  kato: number,
  modulesType: number,
  year?: number | null,
): Promise<IndicatorRow[]> => {
  const params: Record<string, number> = { kato, modulesType };
  if (year != null) {
    params.year = year;
  }
  const response = await api.get<unknown>('/public/indicators/paired', {
    params,
  });
  return normalizeIndicatorsResponse(response.data);
};

/**
 * «Кадровые ресурсы» скрининга (`/public/screening` → human_resources):
 * populationDynamic / populationByAge / employment /
 * populationBySocialStatus. Свободный jsonb — имена полей принимаются
 * и в snake_case, и в camelCase; нет блока → null.
 */
export const normalizeScreeningResponse = (
  raw: unknown,
): ScreeningHumanResources | null => {
  const root = asObj(raw);
  const hrRaw = root.human_resources ?? root.humanResources;
  if (hrRaw == null) return null;
  const hr = asObj(hrRaw);

  const pick = (obj: Record<string, unknown>, ...keys: string[]): unknown => {
    for (const key of keys) {
      if (obj[key] != null) return obj[key];
    }
    return null;
  };

  const employment = hr.employment == null ? null : asObj(hr.employment);
  const social =
    hr.populationBySocialStatus == null
      ? null
      : asObj(hr.populationBySocialStatus);

  const seenYears = new Set<number>();
  return {
    populationDynamic: asArr(hr.populationDynamic)
      .flatMap((entry) => {
        const e = asObj(entry);
        const year = numLoose(e.year);
        if (year === null || seenYears.has(year)) return [];
        seenYears.add(year);
        return [{ year, value: numLoose(e.population) }];
      })
      .sort((a, b) => a.year - b.year),
    populationByAge: asArr(hr.populationByAge).flatMap((entry) => {
      const e = asObj(entry);
      const age = str(e.age);
      if (age === null) return [];
      return [{ age, population: numLoose(e.population) }];
    }),
    employment: employment && {
      hiredBudget: numLoose(pick(employment, 'hired_budget', 'hiredBudget')),
      hiredPrivate: numLoose(pick(employment, 'hired_private', 'hiredPrivate')),
      selfEmployed: numLoose(pick(employment, 'selfEmployed', 'self_employed')),
      unemployed: numLoose(employment.unemployed),
      workingElderly: numLoose(
        pick(employment, 'workingElderly', 'working_elderly'),
      ),
    },
    populationBySocialStatus: social && {
      tsaRecipients: numLoose(
        pick(social, 'TSArecipients', 'tsaRecipients', 'TSARecipients'),
      ),
      pensioners: numLoose(social.pensioners),
      largeFamilies: numLoose(pick(social, 'largeFamilies', 'large_families')),
      // «Лица с инвалидностью» — сумма 1/2/3 групп; ни одного
      // значения → null (строка виджета покажет «—»).
      disabled: (() => {
        const groups = [
          numLoose(
            pick(social, 'firstGroupDisabled', 'first_group_disabled', 'firstGroupDisab'),
          ),
          numLoose(
            pick(social, 'secondGroupDisabled', 'second_group_disabled', 'secondGroupDisab'),
          ),
          numLoose(
            pick(social, 'thirdGroupDisabled', 'third_group_disabled', 'thirdGroupDisab'),
          ),
        ];
        return groups.some((v) => v !== null)
          ? groups.reduce<number>((acc, v) => acc + (v ?? 0), 0)
          : null;
      })(),
    },
  };
};

/**
 * «Развитие МСБ» скрининга (`smb_development`, 17.4): mcbSubjects,
 * creditSupport, registeredSubjects. Особенности staging-данных:
 * числа приходят строками («» → null), в registeredSubjects кроме
 * фиксированных отраслей бывают динамические ключи (`createdAt-<ts>`)
 * с объектом `{value, labelRu, labelKz}` — подписи берутся из них.
 */
export const normalizeScreeningSmb = (
  raw: unknown,
): ScreeningSmbDevelopment | null => {
  const root = asObj(raw);
  const smbRaw = root.smb_development ?? root.smbDevelopment;
  if (smbRaw == null) return null;
  const smb = asObj(smbRaw);
  const subjects = smb.mcbSubjects == null ? null : asObj(smb.mcbSubjects);
  const credit = smb.creditSupport == null ? null : asObj(smb.creditSupport);

  const registeredSubjects: SmbRegisteredSubject[] = Object.entries(
    asObj(smb.registeredSubjects),
  ).flatMap(([key, entry]) => {
    if (typeof entry === 'object' && entry !== null) {
      const e = asObj(entry);
      const value = numLoose(e.value);
      if (value === null) return [];
      return [
        { key, label_ru: str(e.labelRu), label_kz: str(e.labelKz), value },
      ];
    }
    const value = numLoose(entry);
    if (value === null) return [];
    return [{ key, label_ru: null, label_kz: null, value }];
  });

  return {
    totalSum: numLoose(smb.totalSum ?? smb.total_sum),
    subjects: subjects && {
      total: numLoose(subjects.total),
      too: numLoose(subjects.TOO ?? subjects.too),
      ip: numLoose(subjects.IP ?? subjects.ip),
      kh: numLoose(subjects.KH ?? subjects.kh),
      spk: numLoose(subjects.SPK ?? subjects.spk),
    },
    creditSupport: credit && {
      subsidizingProjects: numLoose(
        credit.subsidizing_projects ?? credit.subsidizingProjects,
      ),
      subsidizingSum: numLoose(credit.subsidizing_sum ?? credit.subsidizingSum),
      guaranteeProjects: numLoose(
        credit.guarantee_projects ?? credit.guaranteeProjects,
      ),
      guaranteeSum: numLoose(credit.guarantee_sum ?? credit.guaranteeSum),
      grantProjects: numLoose(credit.grant_projects ?? credit.grantProjects),
      grantSum: numLoose(credit.grant_sum ?? credit.grantSum),
    },
    registeredSubjects,
  };
};

/**
 * Оба нормализованных блока скрининга; запись без единого известного
 * блока (или тело null) → null, чтобы вызывающий код мог перезапросить
 * без года (последняя запись).
 */
export const getScreening = async (
  kato: number,
  year?: number | null,
): Promise<ScreeningResponse | null> => {
  const params: Record<string, number> = { kato };
  if (year != null) {
    params.year = year;
  }
  const response = await api.get<unknown>('/public/screening', { params });
  const humanResources = normalizeScreeningResponse(response.data);
  const smbDevelopment = normalizeScreeningSmb(response.data);
  if (humanResources === null && smbDevelopment === null) return null;
  return { humanResources, smbDevelopment };
};

/**
 * Первичные данные показателя для drill-through (US-02 FR-4.6):
 * `/public/general/indicator-detail?kato=&type=&subtype=`. Год эндпоинт
 * не принимает — отдаёт записи по территории.
 */
export const getIndicatorDetail = async (
  kato: number,
  type: string,
  subtype: string,
): Promise<IndicatorDetailResponse> => {
  const response = await api.get<unknown>('/public/general/indicator-detail', {
    params: { kato, type, subtype },
  });
  return normalizeIndicatorDetail(response.data);
};

export const getMediaMonitoring = async (
  kato: number,
): Promise<PublicMediaMonitoringResponse> => {
  const response = await api.get<unknown>('/public/media-monitoring', {
    params: { kato },
  });
  return normalizeMediaMonitoringResponse(response.data);
};

export const getKatoCoordinates = async (): Promise<KatoCoordinatesResponse> => {
  const response = await api.get<unknown>('/public/kato/coordinates');
  return normalizeKatoCoordinatesResponse(response.data);
};

export const getKatoList = async (): Promise<{ items: KatoListItem[] }> => {
  const response = await api.get<unknown>('/public/kato/list');
  return normalizeKatoList(response.data);
};

/**
 * `village_total` по КАТО из `/public/kato/statistics`
 * (`{statistics: {"<kato>": {village_total, ...}}}`): количество дочерних
 * единиц территории (у области — районы, у района — с.о., у с.о. — сёла).
 * Остальные поля записи (required/completed/...) на бэке пока не заполняются.
 */
export const normalizeKatoStatistics = (
  raw: unknown,
): Record<string, number> => {
  const stats = asObj(asObj(raw).statistics);
  const out: Record<string, number> = {};
  for (const [kato, entry] of Object.entries(stats)) {
    const e = asObj(entry);
    const value = numLoose(e.village_total ?? e.villageTotal);
    if (value !== null) out[kato] = value;
  }
  return out;
};

// Ответ statistics одинаков для всех КАТО (параметр kato игнорируется
// бэком) — кэшируем один запрос на сессию; ошибка сбрасывает кэш.
let katoStatisticsCache: Promise<Record<string, number>> | null = null;

export const getKatoStatistics = (): Promise<Record<string, number>> => {
  if (katoStatisticsCache === null) {
    katoStatisticsCache = api
      .get<unknown>('/public/kato/statistics')
      .then((response) => normalizeKatoStatistics(response.data))
      .catch((err: unknown) => {
        katoStatisticsCache = null;
        throw err;
      });
  }
  return katoStatisticsCache;
};

/**
 * Флаги видимости модулей для авторизованного пользователя (US-09):
 * `/public/modules` → `{modules: [{module_id, enabled}]}`. Записи без
 * валидного `module_id` отбрасываются; `enabled` не-boolean трактуем как
 * `true` (default-visible, как в `useModulePresent`).
 */
export interface PublicModuleInfo {
  module_id: string;
  enabled: boolean;
}

export const listPublicModules = async (
  /**
   * Территория, для которой считается видимость: супер-админ задаёт её
   * отдельно на каждый уровень КАТО (область / район-город / сельский
   * округ / село, DP-365). Без `kato` бэкенд отвечает по умолчанию — и
   * настройки уровня на витрину не попадают.
   */
  kato?: number | null,
): Promise<PublicModuleInfo[]> => {
  const response = await api.get<unknown>('/public/modules', {
    params: kato != null ? { kato } : undefined,
  });
  return asArr(asObj(response.data).modules).flatMap((entry) => {
    const e = asObj(entry);
    const moduleId = str(e.module_id);
    if (moduleId === null) return [];
    return [{ module_id: moduleId, enabled: e.enabled !== false }];
  });
};
